import { LoadingOutlined } from '@ant-design/icons';
import MantencionDownload from 'PDF/Mantencion/MantencionDowload';
import InformeMantencionDownload from 'PDF/documents/informeMantencion/InformeMantencionDownload';
import useTemplateOrFallback from 'PDF/TemplateEngine/useTemplateOrFallback';
import { resolveDocDesign } from 'PDF/TemplateEngine/resolveDocDesign';
import useGenerateAndSendPDF from 'PDF/Mantencion/useMantencionSend';
import { Modal, notification } from 'antd';
import { SOLICITUD_TIPOS } from 'assets/data/SolicitudesEstados';
import axios from 'axios';
import { API_PATH } from 'config';
import { requestMantencion, useAppApi } from 'hooks/useAppApi';
import { useOfflineSolicitudes } from 'hooks/useOfflineSolicitudes';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import paths from 'assets/routes/paths';
import { getAccessTokenApi } from '../api/auth';
import { getStaticMantencionData } from '../api/budget';
import { useVisita } from 'hooks/useVisita';

const SolicitudesMantencionContext = createContext(null);

export const SolicitudesMantencionProvider = ({ children, id }) => {
  const map = useCallback((result) => {
    return result;
  }, []);

  const { data, isLoading, error, hasCalled, call, servedFromCache } = useOfflineSolicitudes();

  const {
    call: callmantencion,
    data: mantencion,
    isLoading: isLoadingMantencion,
  } = useAppApi({
    baseUrl: '/mantenciones',
    mapResults: map,
  });
  const navigate = useNavigate();
  const { iniciarVisita } = useVisita();

  const { template: informeTemplate } = useTemplateOrFallback('informe_mantencion');

  const { call: notFunctionalStartMan, isLoading: isLoadingStartMan } = useAppApi({
    baseUrl: '/solicitudes',
    mapResults: useCallback((data) => {
      return data;
    }, []),
  });
  const { generateAndSendPDF, loading: isLoadingSendPDF } = useGenerateAndSendPDF();

  const openDowloadMantencion = async (solicitud) => {
    await callmantencion({
      extUrl: `/${solicitud.id_mantencion}/document-data`,
    });
    setVisible(true);
  };

  const requestStart = async (solicitudId, confirm = false) => {
    const token = getAccessTokenApi();
    const url = `${API_PATH}/solicitudes/${solicitudId}/mantenciones/start${
      confirm ? '?confirm=1' : ''
    }`;
    return axios.get(url, { headers: { Authorization: token } });
  };

  const startMantSolc = async (solicitud) => {
    if ([SOLICITUD_TIPOS.EN_CURSO].includes(solicitud?.status)) {
      const baseUrl = paths.specialist.mantenciones.detail(solicitud.id_mantencion);
      const queryParam = solicitud.id_mantencion_anterior
        ? `?mantencion_anterior=${solicitud.id_mantencion_anterior}`
        : '';
      navigate(`${baseUrl}${queryParam}`);
      return;
    }
    if ([SOLICITUD_TIPOS.ENVIADA, SOLICITUD_TIPOS.FINALIZADA].includes(solicitud.status)) {
      await callmantencion({
        extUrl: `/${solicitud.id_mantencion}/document-data`,
      });
      setVisible(true);
      return;
    }
    if (![SOLICITUD_TIPOS.ASIGNADA].includes(solicitud.status)) {
      notification.error({
        message: 'Estado de la solicitud no permite iniciar la mantencion',
      });
      return;
    }

    try {
      const response = await requestStart(solicitud.id, false);
      await call();
      return response.data;
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 409 && data?.code === 'SIN_PLAN_PARCIAL') {
        const listaHtml = (data.ascensoresSinPlan || []).map((a) => `• ${a.nombre}`).join('\n');
        Modal.confirm({
          title: 'Algunos ascensores no tienen plan de mantención',
          content: (
            <div>
              <p>{data.mensaje}</p>
              <p style={{ marginTop: 8, fontWeight: 500 }}>Ascensores sin plan:</p>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: 8,
                  borderRadius: 4,
                  margin: 0,
                }}
              >
                {listaHtml}
              </pre>
            </div>
          ),
          okText: 'Continuar igual',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              const retry = await requestStart(solicitud.id, true);
              await call();
              notification.success({
                message: 'Mantención iniciada',
                description: `Se creó la mantención para ${data.conPlan} ascensor(es) con plan.`,
              });
              return retry.data;
            } catch (retryErr) {
              notification.error({
                message: 'Error al iniciar la mantención',
                description: retryErr?.response?.data?.mensaje || 'Ocurrió un error inesperado.',
              });
            }
          },
        });
        return;
      }

      if (status === 400) {
        notification.error({
          message: 'No se puede iniciar la mantención',
          description: data?.mensaje || 'El cliente no cumple los requisitos.',
          duration: 8,
        });
        return;
      }

      notification.error({
        message: 'Error al iniciar la mantención',
        description: data?.mensaje || err?.message || 'Ocurrió un error inesperado.',
      });
    }
  };

  // Usada por el drawer de confirmación: crea la mantención y, si sale bien,
  // navega DIRECTO al checklist (elimina el doble click). Los errores 400/409
  // se devuelven como objeto para que el drawer los muestre inline en vez del
  // Modal.confirm/notification global de startMantSolc.
  const iniciarMantencionConfirmada = async (solicitud, { confirm = false, lat, lng } = {}) => {
    try {
      const response = await requestStart(solicitud.id, confirm);
      const mantencion = response.data;
      await call();
      const idMantencion = mantencion?.id ?? solicitud.id_mantencion;
      // Registrar el inicio de visita desde la ubicación capturada en el drawer.
      // Best-effort: si falla (red / GPS ausente) no bloquea la navegación; el
      // checklist mostrará el botón "Iniciar visita" como fallback.
      if (idMantencion) {
        try {
          await iniciarVisita({ tipo: 'maintenance', id: idMantencion, lat, lng });
        } catch (visitaErr) {
          // best-effort: se ignora
        }
      }
      navigate(paths.specialist.mantenciones.detail(idMantencion));
      return { ok: true, mantencion };
    } catch (err) {
      return {
        ok: false,
        status: err?.response?.status,
        code: err?.response?.data?.code,
        data: err?.response?.data,
      };
    }
  };

  const [config, setConfig] = useState({});

  const [visible, setVisible] = useState(false);

  const [staticData, setStaticData] = useState(null);
  const token = getAccessTokenApi();
  useEffect(() => {
    getStaticMantencionData(token).then((result) => {
      setStaticData(result.static);
      setConfig(result.tenant);
    });
  }, [token]);

  const handleSendPDF = async (solicitud) => {
    const response = await requestMantencion(solicitud.id_mantencion);
    const mantencion = response.data;
    // Solo auto-enviar si el cliente tiene el flag activado. El destinatario
    // real lo resuelve el backend (Cliente.email); pasamos null → modo auto.
    if (!mantencion?.mantencion?.Cliente?.envio_auto_mantencion) return;
    await generateAndSendPDF(mantencion, staticData, null, solicitud.id_mantencion);
  };
  return (
    <>
      <Modal open={visible} onCancel={() => setVisible(false)} footer={null}>
        {isLoadingMantencion ? (
          <LoadingOutlined />
        ) : resolveDocDesign(mantencion?.mantencion, informeTemplate) === 'lowcode' ? (
          <InformeMantencionDownload mantencion={mantencion} />
        ) : (
          <MantencionDownload mantencion={mantencion} />
        )}
      </Modal>
      <SolicitudesMantencionContext.Provider
        value={{
          call,
          id,
          isLoading,
          error,
          hasCalled,
          data,
          servedFromCache,
          startMantSolc,
          iniciarMantencionConfirmada,
          isLoadingStartMan,
          openDowloadMantencion,
          handleSendPDF,
        }}
      >
        {children}
      </SolicitudesMantencionContext.Provider>
    </>
  );
};

export const useSolicitudesMantencion = () => useContext(SolicitudesMantencionContext);
